import './index.css'
import { Controller as BaseController } from 'stimulus'
import Sortable from 'sortablejs'
import I18n from '@app/javascript/i18n/index.js.erb'
import Highcharts from 'highcharts'
import Rails from '@rails/ujs'
import { flashError } from '@app/javascript/classes/flashMessages'

export class Controller extends BaseController {
  static targets = [
    'self', 'container', 'requestInfo', 'list', 'topHeader', 'gridHeader', 'gridContainer',
    'barChart', 'resetButton', 'revertButton', 'saveButton', 'sortButton', 'orderButton',
    'sortButtonText', 'orderButtonText', 'orderButtonIcon'
  ]

  static classes = ['loading']

  static values = {
    loaded: Boolean,
    fetchUrl: String,
    saveUrl: String,
    scope: String,
    chamber: String,
    flyout: Boolean,
    predictionCount: Object,
    isDirty: Boolean,
    isOriginal: Boolean,
    sort: String,
    order: String,
    readOnly: Boolean
  }

  sortables = []
  sortingValues = {}

  connect() {
    if (!this.loadedValue) {
      this.requestInfoTarget.innerHTML = I18n.t('loading_dots')
      return this.fetchWhipboard(
        () => { this.requestInfoTarget.innerHTML = I18n.t('entities.show.overview.field.us_bill.whipboard.load_failure') }
      )
    }

    if (this.flyoutValue || this.readOnlyValue) return

    const boundHandleDrop = this.handleDrop.bind(this)
    this.listTargets.forEach(ul => {
      this.sortables.push(
        Sortable.create(ul, {
          group: `whipboard-${this.scopeValue}`,
          animation: 300,
          onEnd: boundHandleDrop
        })
      )

      $(ul).children('[data-voter]').each((i, el) => {
        this.sortingValues[el.dataset.id] = { name: el.dataset.name, party: el.dataset.party }
      })
    })

    this.updateHeaderTitle()
    this.updateGraph()
  }

  fetchWhipboard(errHandler, resetToOriginal = false) {
    this.disableAllButtons()

    Rails.ajax({
      type: 'GET',
      dataType: 'script',
      url: this.fetchUrlValue,
      data: $.param({
        element_id: this.selfTarget.id,
        force_original: resetToOriginal
      }),
      error: () => {
        this.enableAllButtons()
        errHandler()
      }
    })
  }

  handleDrop(event) {
    this.handleMove(event.item.dataset.id, event.from.dataset.prediction, event.to.dataset.prediction)
  }

  moveCard(event) {
    const elementToMove = event.currentTarget.closest('[data-voter]')
    const fromList = elementToMove.closest('[data-prediction]')
    const targetList = this.listTargets.find(list => list.dataset.prediction === event.currentTarget.dataset.value)
    targetList.prepend(elementToMove)

    this.handleMove(elementToMove.dataset.id, fromList.dataset.prediction, targetList.dataset.prediction)
  }

  handleMove(legId, from, to) {
    this.isDirtyValue = true
    this.isOriginalValue = false

    const newValues = Object.assign({}, this.predictionCountValue)
    newValues[from] -= 1
    newValues[to] += 1

    this.predictionCountValue = newValues
  }

  handleReset() {
    this.fetchWhipboard(
      () => { flashError(I18n.t('entities.show.overview.field.us_bill.whipboard.original_load_failure')) },
      true
    )
  }

  handleRevert() {
    this.fetchWhipboard(
      () => { flashError(I18n.t('entities.show.overview.field.us_bill.whipboard.revert_load_failure')) }
    )
  }

  handleSave() {
    this.disableAllButtons()

    const virtualWhipboard = this.listTargets.reduce(
      (obj, list) => {
        obj[list.dataset.prediction] = []

        list.children.forEach(child => obj[list.dataset.prediction].push(child.dataset.id))
        return obj
      }, {})

    Rails.ajax({
      type: 'post',
      dataType: 'script',
      url: this.saveUrlValue,
      data: $.param({ chamber: this.chamberValue, virtual_whipboard: virtualWhipboard, element_id: this.selfTarget.id }),
      error: () => {
        flashError(I18n.t('entities.show.overview.field.us_bill.whipboard.save.generic_error'))
        this.enableAllButtons()
      }
    })
  }

  sortWhipboard(attribute, order = 'ascending') {
    this.isDirtyValue = true

    this.sortables.forEach(sortable => {
      const ids = sortable.toArray().sort((left, right) => {
        const leftValue = this.sortingValues[left][attribute]
        const rightValue = this.sortingValues[right][attribute]

        return (order === 'ascending' ? 1 : -1) * leftValue.localeCompare(rightValue)
      })

      sortable.sort(ids, true)
    })
  }

  changeSort(event) {
    this.sortValue = event.currentTarget.dataset.attribute

    this.sortWhipboard(this.sortValue, this.orderValue)
  }

  changeOrder(event) {
    this.orderValue = event.currentTarget.dataset.order

    this.sortWhipboard(this.sortValue, this.orderValue)
  }

  predictionCountValueChanged() {
    if (!this.loadedValue) return

    this.updateHeaderTitle()
    this.updateGraph()
  }

  isDirtyValueChanged() {
    if (!this.loadedValue) return

    [this.revertButtonTarget, this.saveButtonTarget]
      .forEach(el => el.classList.toggle('hidden', !this.isDirtyValue))
  }

  isOriginalValueChanged() {
    if (this.flyoutValue || this.readOnlyValue || !this.loadedValue) return

    this.resetButtonTarget.classList.toggle('hidden', this.isOriginalValue)
  }

  sortValueChanged() {
    if (!this.loadedValue) return

    this.sortButtonTextTarget.innerHTML = I18n.t('entities.show.overview.field.us_bill.whipboard.sort.label',
      { value: I18n.t(`entities.show.overview.field.us_bill.whipboard.sort.${this.sortValue}`) })
  }

  orderValueChanged() {
    if (!this.loadedValue) return

    this.orderButtonTextTarget.innerHTML = I18n.t(`entities.show.overview.field.us_bill.whipboard.sort.${this.orderValue}`)
    this.orderButtonIconTarget.classList.toggle('h-transform-rotate-90', this.orderValue === 'ascending')
    this.orderButtonIconTarget.classList.toggle('h-transform-rotate-270', !(this.orderValue === 'ascending'))
  }

  disableAllButtons() {
    if (!this.loadedValue) return

    [this.resetButtonTarget, this.revertButtonTarget, this.saveButtonTarget].forEach(btn => { btn.disabled = true })
    this.gridContainerTarget.classList.add(this.loadingClass)
    this.sortables.forEach(sortable => sortable.option('disabled', true))
  }

  enableAllButtons() {
    if (!this.loadedValue) return;

    [this.resetButtonTarget, this.revertButtonTarget, this.saveButtonTarget].forEach(btn => { btn.disabled = false })
    this.gridContainerTarget.classList.remove(this.loadingClass)
    this.sortables.forEach(sortable => sortable.option('disabled', false))
  }

  updateHeaderTitle() {
    Object.keys(this.predictionCountValue).forEach((prediction, i) => {
      [this.topHeaderTargets, this.gridHeaderTargets].forEach(headers => {
        headers.find(el => el.dataset.prediction === prediction)
          .innerHTML = I18n.t(
            `entities.show.overview.field.us_bill.whipboard.counted_vote_predictions.${prediction}`,
            { count: this.predictionCountValue[prediction] }
          )
      })
    })
  }

  updateGraph() {
    Highcharts.chart(this.barChartTarget, {
      chart: {
        type: 'bar',
        height: 32,
        margin: [0, 0, 0, 0]
      },
      colors: ['#b34400', '#e47800', '#939598', '#2fa3ad', '#037d85'],
      title: {
        text: null
      },
      subtitle: {
        text: null
      },
      exporting: {
        enabled: false
      },
      credits: {
        enabled: false
      },
      tooltip: {
        enabled: false
      },
      legend: {
        enabled: false
      },
      yAxis: {
        visible: false
      },
      xAxis: {
        visible: false
      },
      plotOptions: {
        series: {
          stacking: 'normal'
        }
      },
      series: Object.values(this.predictionCountValue)
        .map(barVal => ({ data: [barVal], enableMouseTracking: false }))
        .reverse()
    })
  }
}
